import { type ReactNode, useId, useMemo } from "react";

import type { DataRoomDealOpportunity } from "@vega-api/generated/types/dataRoomDealOpportunity";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

import type { AddedNodeSummary } from "./utils/treeUtils";
import type { PermissionChange } from "./types";

type PendingChangesModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  changes: PermissionChange[];
  dealOpportunities: DataRoomDealOpportunity[];
  addedNodeSummaries: AddedNodeSummary[];
  replacedNodes: { id: string; path: string }[];
  deletedNodes: { id: string; path: string; type: "file" | "folder" }[];
  onConfirm: () => void;
  isSaving: boolean;
};

type PendingChangesSectionProps = {
  children: ReactNode;
  title: string;
};

function getChangeLabel(
  type: PermissionChange["type"],
  value: boolean,
): string {
  if (type === "access") {
    return value
      ? m.coInvestments_dataRoom_access()
      : m.coInvestments_dataRoom_noAccess();
  }

  return value
    ? m.coInvestments_dataRoom_fileToggle_downloadable()
    : m.coInvestments_dataRoom_fileToggle_viewOnly();
}

function getAddedSummaryLabel({
  count,
  isRoot,
  locationPath,
  type,
}: AddedNodeSummary): string {
  const location = isRoot ? m.coInvestments_dataRoom_root() : locationPath;

  if (type === "file") {
    return count === 1
      ? m.coInvestments_dataRoom_fileAddedToLocation({
          count,
          location,
        })
      : m.coInvestments_dataRoom_filesAddedToLocation({
          count,
          location,
        });
  }

  return count === 1
    ? m.coInvestments_dataRoom_folderAddedToLocation({
        count,
        location,
      })
    : m.coInvestments_dataRoom_foldersAddedToLocation({
        count,
        location,
      });
}

function PendingChangesSection({
  children,
  title,
}: PendingChangesSectionProps) {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="mt-4 first:mt-0">
      <h4
        className="body-small-semibold mb-2 text-center uppercase tracking-wide text-text/secondary"
        id={titleId}
      >
        {title}
      </h4>
      <ul className="m-0 list-none p-0">{children}</ul>
    </section>
  );
}

export function PendingChangesModal({
  open,
  setOpen,
  changes,
  dealOpportunities,
  addedNodeSummaries,
  replacedNodes,
  deletedNodes,
  onConfirm,
  isSaving,
}: PendingChangesModalProps) {
  const dealOpportunityMap = useMemo(
    () =>
      new Map(
        dealOpportunities.map((dealOpportunity) => [
          dealOpportunity.id,
          dealOpportunity,
        ]),
      ),
    [dealOpportunities],
  );

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: onConfirm,
        mainActionText: m.coInvestments_dataRoom_confirmSave(),
        disabled: isSaving,
        secondaryAction: () => setOpen(false),
        secondaryActionText: m.common_cancel(),
      }}
      dataTestId="data-room-pending-changes-modal"
      open={open}
      setOpen={setOpen}
      title={m.coInvestments_dataRoom_confirmSave()}
    >
      <div className="px-4 py-3 text-left">
        {addedNodeSummaries.length > 0 ? (
          <PendingChangesSection title={m.coInvestments_dataRoom_added()}>
            {addedNodeSummaries.map((summary) => (
              <li
                className="body-base-regular py-1 text-text/secondary"
                data-testid={`pending-change-added-${summary.id}`}
                key={summary.id}
              >
                {getAddedSummaryLabel(summary)}
              </li>
            ))}
          </PendingChangesSection>
        ) : null}
        {replacedNodes.length > 0 ? (
          <PendingChangesSection title={m.coInvestments_dataRoom_updated()}>
            {replacedNodes
              .toSorted((a, b) => a.path.localeCompare(b.path))
              .map((node) => (
                <li
                  className="body-base-regular min-w-0 truncate py-1 text-text/secondary"
                  data-testid={`pending-change-replaced-${node.id}`}
                  key={node.id}
                >
                  {node.path}
                </li>
              ))}
          </PendingChangesSection>
        ) : null}
        {deletedNodes.length > 0 ? (
          <PendingChangesSection title={m.common_deleted()}>
            {deletedNodes
              .toSorted((a, b) => a.path.localeCompare(b.path))
              .map((node) => (
                <li
                  className="body-base-regular min-w-0 truncate py-1 text-text/negative"
                  data-testid={`pending-change-deleted-${node.id}`}
                  key={node.id}
                >
                  {node.path}
                </li>
              ))}
          </PendingChangesSection>
        ) : null}
        {changes.length > 0 ? (
          <PendingChangesSection
            title={m.coInvestments_dataRoom_permissionChanges()}
          >
            {changes
              .toSorted((a, b) => {
                const lpNameA = a.dealOpportunityId
                  ? (dealOpportunityMap.get(a.dealOpportunityId)?.limitedPartner
                      .name ?? "")
                  : "";
                const lpNameB = b.dealOpportunityId
                  ? (dealOpportunityMap.get(b.dealOpportunityId)?.limitedPartner
                      .name ?? "")
                  : "";

                return (
                  lpNameA.localeCompare(lpNameB) ||
                  a.fileName.localeCompare(b.fileName)
                );
              })
              .map((change, index) => {
                const lpName =
                  change.type === "access" && change.dealOpportunityId
                    ? dealOpportunityMap.get(change.dealOpportunityId)
                        ?.limitedPartner.name
                    : null;

                return (
                  <li
                    data-testid={`pending-change-${index}`}
                    key={`${change.type}-${change.fileId}-${change.dealOpportunityId ?? "dl"}`}
                  >
                    <div className="flex items-center gap-2 py-1">
                      {lpName ? (
                        <span className="body-small-medium shrink-0 text-text/primary">
                          {lpName}
                        </span>
                      ) : null}
                      <span className="body-base-regular min-w-0 flex-1 truncate text-left text-text/secondary">
                        {change.fileName}
                      </span>
                      <span className="body-small-regular shrink-0 text-text/tertiary">
                        {getChangeLabel(change.type, change.from)}
                      </span>
                      <span className="shrink-0 text-text/tertiary">→</span>
                      <span className="body-small-medium shrink-0 text-brand-default">
                        {getChangeLabel(change.type, change.to)}
                      </span>
                    </div>
                  </li>
                );
              })}
          </PendingChangesSection>
        ) : null}
      </div>
    </ActionaryModal>
  );
}
